import { Button, Checkbox, Modal, Switch, Tooltip } from "antd";
import { useEffect, useState } from "react";
import Schedule from "ui/automation/schedule";
import { showToast } from "ui/components/common";
import {
  saveMonitoringSchedule,
  getCameraListBySite,
  batchChangeEnable,
} from "../nvrs/service";
import { CameraInfo } from "lib/types";
import clsx from "clsx";
import { SvgCamera, SvgMonitor, SvgToastInfo } from "ui/svgs";
import { isCameraExpired } from "lib/utils";

import { useParams } from "react-router-dom";
import { LoadingOutlined } from "@ant-design/icons";
function Footer({ valid, onClose, onConfirm, loading, onDisableAllCameras }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        marginTop: "24px",
      }}
    >
      <div>
        <Button
          onClick={onDisableAllCameras}
          className={"primaryButton danger bordered"}
          style={{ marginLeft: "12px" }}
        >
          Disable All
        </Button>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div className="primaryButton bordered" onClick={onClose}>
          Cancel
        </div>
        <Button
          loading={loading}
          onClick={onConfirm}
          disabled={!valid}
          className={valid ? "primaryButton" : "primaryButton disabled"}
          style={{ marginLeft: "12px" }}
        >
          Confirm
        </Button>
      </div>
    </div>
  );
}
export default function SiteMonitoringModal({
  visible,
  onClose,
  onRefresh,
  site,
}) {
  const { orgid } = useParams();
  const [schedule, setSchedule] = useState([]);
  const [cameraList, setCameraList] = useState<CameraInfo[]>([]);
  const [availableCameraList, setAvailableCameraList] = useState<CameraInfo[]>(
    [],
  );
  const [checkedCamera, setCheckedCamera] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enableMonitoring, setEnableMonitoring] = useState(false);
  useEffect(() => {
    if (visible && site) {
      setLoading(true);
      getCameraListBySite(site.id, orgid)
        .then((res) => {
          if (res.status === 200) {
            if (!res.data?.data?.length) {
              showToast(1, "No cameras found in this site");
              onClose();
            }
            setCameraList(res.data.data);
            const availableCameraList = res.data.data.filter((camera, i) => {
              const monitoringInfo =
                camera?.cloudCapabilities?.funcsEnabled
                  ?.goods_unit_type_monitoring;
              return monitoringInfo?.enable;
            });
            setAvailableCameraList(availableCameraList);
          } else {
            showToast(1, "Failed to get camera list");
            onClose();
          }
        })
        .finally(() => setLoading(false));
    }
    if (!visible) {
      setCameraList([]);
      setCheckedCamera([]);
    }
  }, [visible, site]);
  async function onSave() {
    const res = await saveMonitoringSchedule(
      checkedCamera,
      {
        schedules: schedule,
        timezone: site?.timezone,
        enableMonitoring: enableMonitoring,
      },
      site?.id,
      orgid,
    );
    if (res.status === 200) {
      showToast(0, "Monitoring schedule saved successfully");
      onClose();
      onRefresh();
    } else {
      showToast(1, "Failed to save monitoring schedule");
    }
  }
  function onSelectAll() {
    if (availableCameraList.length === checkedCamera.length) {
      setCheckedCamera([]);
      return;
    } else setCheckedCamera(availableCameraList.map((x) => x.id));
  }
  function onCheck(id, checked) {
    setCheckedCamera((state) => {
      if (!checked) {
        return state.filter((x) => x !== id);
      }
      return [...state, id];
    });
  }
  function onDisableAllCameras() {
    Modal.confirm({
      title: "Confirm disable all cameras",
      content: "Are you sure you want to disable all cameras monitor?",
      async onOk() {
        const res = await batchChangeEnable(
          availableCameraList.map((x) => x.id),
          false,
          site?.id,
          orgid,
        );
        if (res.status === 200) {
          showToast(0, "Disable all cameras monitoring success");
          onClose();
          onRefresh?.();
        } else {
          showToast(1, "Failed to disable all cameras monitoring");
        }
      },
      okText: "Disable",
      okButtonProps: {
        className: "primaryButton ",
      },
      cancelButtonProps: {
        className: "primaryButton bordered",
      },
    });
  }
  return (
    <Modal
      width={800}
      open={visible}
      title="Manage Site Camera Monitoring"
      onCancel={onClose}
      footer={
        <Footer
          valid={checkedCamera.length && schedule.length}
          onClose={onClose}
          onConfirm={onSave}
          loading={loading}
          onDisableAllCameras={onDisableAllCameras}
        />
      }
    >
      <div style={{ marginTop: "18px" }}>
        <span style={{ fontSize: "16px", fontWeight: "600" }}>
          Site Timezone:{" " + site?.timezone}
        </span>
      </div>
      <div
        style={{
          marginTop: "12px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontSize: "14px" }}>Select Cameras</span>
        <div className="flex items-center">
          <SvgMonitor width="18" className="mr-1" />
          <span>
            {(site.stats?.underMonitorCamNum || 0) +
              "/" +
              (site.stats?.enableMonitorCamNum || 0)}
          </span>
        </div>
      </div>
      <div
        style={{
          width: "100%",
          marginTop: "4px",
          border: "1px solid rgba(189, 189, 189, 1)",
          borderRadius: "16px",
          padding: "0px 20px 8px 20px",
          position: "relative",
          minHeight: "40px",
        }}
      >
        {loading && (
          <div
            className="mask"
            style={{
              textAlign: "center",
              borderRadius: "16px",
              backgroundColor: "transparent",
            }}
          >
            <LoadingOutlined style={{ color: "black" }} />
          </div>
        )}
        <Button
          type="link"
          style={{ paddingLeft: "0px" }}
          onClick={onSelectAll}
        >
          select all
        </Button>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "4px",
          }}
        >
          {cameraList.map((item, i) => {
            const monitoringInfo =
              item?.cloudCapabilities?.funcsEnabled?.goods_unit_type_monitoring;
            const title = monitoringInfo?.reason || "";

            return (
              <div className="flex items-center mt-2" key={item.id}>
                <Tooltip title={title}>
                  <Checkbox
                    disabled={!monitoringInfo?.enable}
                    checked={checkedCamera.includes(item.id)}
                    onChange={(e) => onCheck(item.id, e.target.checked)}
                  >
                    <div
                      className={clsx(
                        "flex items-center gap-1 fill-zinc-500 relative top-0.5",
                        { "opacity-50": !monitoringInfo?.enable },
                      )}
                    >
                      <SvgCamera width="18" className="flex-shrink-0" />
                      {item.name}
                    </div>
                  </Checkbox>
                </Tooltip>
                {/* {expired && (
                  <Tooltip title={title}>
                    <div>
                      <SvgToastInfo width="16" className="fill-zinc-500" />
                    </div>
                  </Tooltip>
                )} */}
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ marginTop: "12px" }}>
        <span style={{ fontSize: "16px", fontWeight: "600" }}>
          Enable Monitoring
        </span>
        <Switch
          checked={enableMonitoring}
          onChange={setEnableMonitoring}
          style={{ marginLeft: "12px" }}
        />
      </div>
      <Schedule alertSchedules={schedule} setAlertSchedules={setSchedule} />
    </Modal>
  );
}
